// 材料使用期限管理表 — 運用管理系
// 在庫中の MaterialQr に対する使用期限管理(期限60日以内警告)。

import ExcelJS from "exceljs";
import type { ExtendedDB } from "../../../../types/extendedDB";
import {
  buildLookups, downloadBlob, todayStr, setHeaderRow, fmtDate,
  BAD_FILL, WARN_FILL, sumContentKg, calcExpiryDate,
} from "./helpers";
import { getQrRemainingRatio, isQrFullyConsumed } from "../../../../util/consumption";

type Lookups = ReturnType<typeof buildLookups>;

function resolveWork(db: ExtendedDB, lk: Lookups, workId?: number) {
  if (workId != null) {
    return lk.workById.get(workId);
  }
  return db.works.find((w) => w.deleted_at == null);
}

function calcDaysLeft(expiryDate: string, today: Date): number | null {
  if (expiryDate) {
    return Math.floor((new Date(expiryDate).getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  }
  return null;
}

type CarryIn = ReturnType<Lookups["carryInById"]["get"]>;

function resolveCarryIn(lk: Lookups, carryInId: string | null | undefined): CarryIn {
  if (carryInId) {
    return lk.carryInById.get(carryInId);
  }
  return undefined;
}

function resolveWorkName(lk: Lookups, carryIn: CarryIn): string {
  if (carryIn && carryIn.work_id != null) {
    return lk.workById.get(carryIn.work_id)?.short_name ?? "";
  }
  return "";
}

function resolveCarryInLocation(lk: Lookups, carryIn: CarryIn): string {
  if (carryIn?.carry_in_location_id != null) {
    return lk.carryInLocationById.get(carryIn.carry_in_location_id)?.name ?? "";
  }
  return "";
}

export async function exportMaterialExpirationTable(db: ExtendedDB, workId?: number): Promise<void> {
  const lk = buildLookups(db);
  const work = resolveWork(db, lk, workId);

  const wb = new ExcelJS.Workbook();
  wb.creator = "奥村組 トレーサビリティシステム";
  const ws = wb.addWorksheet("材料使用期限管理表");

  ws.getCell("A1").value = "材料使用期限管理表";
  ws.getCell("A1").font = { bold: true, size: 14 };
  if (work) {
    ws.getCell("A2").value = "工事:";
    ws.getCell("A2").font = { bold: true };
    ws.getCell("B2").value = work.official_name;
  }
  ws.getCell("A3").value = "出力日:";
  ws.getCell("A3").font = { bold: true };
  ws.getCell("B3").value = todayStr();
  ws.getCell("D3").value = "凡例:";
  ws.getCell("D3").font = { bold: true };
  ws.getCell("E3").value = "期限60日以内";
  ws.getCell("E3").fill = WARN_FILL;
  ws.getCell("F3").value = "期限切れ";
  ws.getCell("F3").fill = BAD_FILL;

  const headers: Array<[string, number]> = [
    ["材料名", 26], ["材料種別", 14], ["LotNo", 14], ["管理番号(範囲)", 18],
    ["数量", 10], ["容量(kg)", 10], ["製造日", 12], ["搬入日", 12], ["使用期限", 12],
    ["残日数", 10], ["状態", 12], ["工事", 32], ["搬入場所", 22], ["備考", 30],
  ];
  const headerRow = 5;
  headers.forEach((h, i) => {
    ws.getCell(headerRow, i + 1).value = h[0];
    ws.getColumn(i + 1).width = h[1];
  });
  setHeaderRow(ws, headerRow);

  type StockRow = {
    materialName: string;
    materialDetailName: string;
    lotNo: string;
    rangeStart?: number;
    rangeEnd?: number;
    count: number;
    capacityKg: number;
    manufacturedAt: string;
    receivedAt: string;
    expiryDate: string;
    daysLeft: number | null;
    workName: string;
    carryInLocation: string;
    remarks: string;
  };
  const rows: StockRow[] = [];

  // 在庫 = 搬入済みで全量消費(Σ used_ratio ≥ 1−ε)に達していない材料QR。全量判定は消費行から都度計算する。
  const stockQrs = db.materialQrs.filter((q) => q.carry_in_id != null && !isQrFullyConsumed(db.constructionMaterialConsumptions, q.id));
  const qrsByLot = new Map<string, typeof stockQrs>();
  for (const q of stockQrs) {
    if (!q.lot_id) continue;
    const arr = qrsByLot.get(q.lot_id) ?? [];
    arr.push(q);
    qrsByLot.set(q.lot_id, arr);
  }

  const today = new Date(todayStr());
  for (const [lotId, qrs] of qrsByLot) {
    const lot = lk.lotById.get(lotId);
    if (!lot || lot.material_id == null) continue;
    const material = lk.materialById.get(lot.material_id);
    if (!material) continue;
    const details = lk.materialDetailsByMaterialId.get(material.id) ?? [];
    const detailName = details.map((d) => d.detail_name).join("+");
    const contentKgs = sumContentKg(details);
    const shelfMonths = details.find((d) => d.shelf_life_months != null)?.shelf_life_months;
    const manufacturedAtStr = lot.manufactured_at != null ? fmtDate(lot.manufactured_at) : "";
    const expiryDate = calcExpiryDate(manufacturedAtStr || undefined, shelfMonths);
    const daysLeft = calcDaysLeft(expiryDate, today);

    const firstQr = qrs[0];
    if (firstQr === undefined) {
      continue;
    }
    const carryIn = resolveCarryIn(lk, firstQr.carry_in_id);
    const workName = resolveWorkName(lk, carryIn);
    if (work && carryIn?.work_id !== work.id) continue;
    const carryInLocation = resolveCarryInLocation(lk, carryIn);

    const ids = qrs.map((qr) => qr.id).sort((a, b) => a - b);

    rows.push({
      materialName: material.name,
      materialDetailName: detailName,
      lotNo: lot.lot_no ?? "",
      rangeStart: ids[0],
      rangeEnd: ids[ids.length - 1],
      count: qrs.length,
      // QR部分消費モデル: 一部消費QRは残割合分のみ計上する。
      capacityKg: qrs.reduce((s, q) => s + getQrRemainingRatio(db.constructionMaterialConsumptions, q.id), 0) * contentKgs,
      manufacturedAt: manufacturedAtStr,
      receivedAt: carryIn ? fmtDate(carryIn.received_at) : "",
      expiryDate,
      daysLeft,
      workName,
      carryInLocation,
      remarks: lot.remarks ?? "",
    });
  }

  rows.sort((a, b) => {
    if (a.daysLeft == null && b.daysLeft == null) return a.materialName.localeCompare(b.materialName);
    if (a.daysLeft == null) return 1;
    if (b.daysLeft == null) return -1;
    return a.daysLeft - b.daysLeft;
  });

  let r = headerRow + 1;
  for (const row of rows) {
    ws.getCell(r, 1).value = row.materialName;
    ws.getCell(r, 2).value = row.materialDetailName;
    ws.getCell(r, 3).value = row.lotNo;
    ws.getCell(r, 4).value = row.rangeStart != null ? `${row.rangeStart}-${row.rangeEnd}` : "";
    ws.getCell(r, 5).value = row.count;
    ws.getCell(r, 6).value = row.capacityKg.toFixed(1);
    ws.getCell(r, 7).value = row.manufacturedAt;
    ws.getCell(r, 8).value = row.receivedAt;
    ws.getCell(r, 9).value = row.expiryDate;
    ws.getCell(r, 10).value = row.daysLeft;
    let status = "正常";
    if (row.daysLeft != null) {
      if (row.daysLeft < 0) status = "期限切れ";
      else if (row.daysLeft <= 60) status = "注意";
    }
    ws.getCell(r, 11).value = status;
    ws.getCell(r, 12).value = row.workName;
    ws.getCell(r, 13).value = row.carryInLocation;
    ws.getCell(r, 14).value = row.remarks;

    if (row.daysLeft != null) {
      if (row.daysLeft < 0) {
        for (let c = 1; c <= headers.length; c++) ws.getCell(r, c).fill = BAD_FILL;
      } else if (row.daysLeft <= 60) {
        for (let c2 = 1; c2 <= headers.length; c2++) ws.getCell(r, c2).fill = WARN_FILL;
      }
    }
    r++;
  }

  if (rows.length === 0) {
    ws.getCell(headerRow + 1, 1).value = "※ 在庫中の材料が存在しません";
    ws.getCell(headerRow + 1, 1).font = { italic: true };
  }

  const buffer = await wb.xlsx.writeBuffer();
  downloadBlob(buffer, `材料使用期限管理表_${todayStr()}.xlsx`);
}
